import { AgentRowLabel } from "@/components/harness/agentRows";
import { ConnectAgentButton } from "@/components/harness/ConnectAgentButton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AGENT_META,
  AGENT_ORDER,
  AgentId,
  findAgent,
} from "@/constants/agents";
import { AgentReasoningEffort } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import { cn } from "@/utils";
import { trackComposerControlChanged } from "@/utils/analytics";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Lock,
} from "lucide-react";
import { useState } from "react";
import { HarnessControl } from "../hooks/useHarnessControl";
import { PermissionModeControl } from "../hooks/usePermissionModeControl";
import {
  AgentSettingsPanel,
  hasAgentSettings,
  useAgentSettingsLabel,
} from "./AgentEffortPill";
import { ConfirmationModeButton } from "./ConfirmationModeButton";
import {
  FOOTER_TRIGGER_CLASS,
  footerMenuPlacement,
  MENU_CLASS,
  MenuSide,
  ROW_CLASS,
} from "./menuStyles";

/**
 * The line under the composer: permissions on the left, where the chat runs on the right. The
 * chip is the one place to set the agent, its model and its thinking: until the first message it
 * opens the agents, each drilling into its settings; after it, straight into the locked agent's.
 */
export function ComposerFooterLine({
  permission,
  harness,
  tier,
  onTierChange,
  onConnect,
  explain = true,
  menuSide = "top",
}: {
  /** Whether Allow all spells out what it means; narrow composers keep only the label. */
  explain?: boolean;
  /** Which way the line's menus open. */
  menuSide?: MenuSide;
  permission: PermissionModeControl;
  harness: HarnessControl;
  /** Cohesive's effort tier, which the chip's settings set. */
  tier: AgentReasoningEffort;
  onTierChange?: (tier: AgentReasoningEffort) => void;
  onConnect: (agent: AgentId) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 px-1 pt-1.5 text-xs">
      <ConfirmationModeButton
        control={permission}
        explain={explain}
        menuSide={menuSide}
      />
      <AgentChip
        control={harness}
        menuSide={menuSide}
        tier={tier}
        onTierChange={onTierChange}
        onConnect={onConnect}
      />
    </div>
  );
}

function AgentChip({
  control,
  menuSide,
  tier,
  onTierChange,
  onConnect,
}: {
  control: HarnessControl;
  menuSide: MenuSide;
  tier: AgentReasoningEffort;
  onTierChange?: (tier: AgentReasoningEffort) => void;
  onConnect: (agent: AgentId) => void;
}) {
  const [open, setOpen] = useState(false);
  // The agent whose settings are drilled into; null is the agent list.
  const [view, setView] = useState<AgentId | null>(null);
  const settingsLabel = useAgentSettingsLabel(control, tier);
  const meta = AGENT_META[control.agent];
  const label = control.connected
    ? [meta.chipLabel, settingsLabel].filter(Boolean).join(" · ")
    : (meta.notConnectedLabel ?? meta.chipLabel);
  const inner = (
    <>
      <meta.Logo className="size-3.5 shrink-0" />
      <span className="truncate">{label}</span>
    </>
  );

  // Until this load's catalog lands the agent is not known; saying Cohesive would be a guess.
  if (control.pending) {
    return (
      <span
        role="status"
        aria-busy
        aria-label="Where this chat runs"
        className="inline-flex h-5 w-20 shrink-0 animate-pulse rounded-md bg-n-10"
      />
    );
  }

  const settable = hasAgentSettings(control);
  if (!control.switchable && !settable) {
    return (
      <span
        className="inline-flex min-w-0 shrink-0 items-center gap-1.5 px-1.5 py-0.5 text-n-50"
        aria-label={`Runs on ${label}`}
      >
        {inner}
      </span>
    );
  }

  const close = () => setOpen(false);
  const pick = (agent: AgentId) => {
    if (agent !== control.agent) {
      trackComposerControlChanged({ control: "agent", value: agent });
    }
    control.setAgent(agent);
  };
  const connect = (agent: AgentId) => {
    close();
    pick(agent);
    onConnect(agent);
  };
  const shown = control.switchable ? view : control.agent;
  const placement = footerMenuPlacement(menuSide);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setView(null);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={
            control.switchable
              ? `Where this chat runs: ${label}`
              : `Runs on ${label}`
          }
          className={cn(
            FOOTER_TRIGGER_CLASS,
            "shrink-0 text-n-50 hover:bg-accent hover:text-n-90",
          )}
        >
          {inner}
          <ChevronDown className="size-3 shrink-0 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={placement.side}
        avoidCollisions={placement.avoidCollisions}
        align="end"
        sideOffset={8}
        className={cn(MENU_CLASS, "w-80", placement.className)}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {shown ? (
          <AgentSettingsView
            agent={shown}
            control={control}
            tier={tier}
            onTierChange={onTierChange}
            onBack={control.switchable ? () => setView(null) : undefined}
            onConnect={() => connect(shown)}
            onDone={close}
          />
        ) : (
          <>
            <div className="px-4 pt-2 pb-1 font-mono text-2xs font-medium uppercase tracking-[0.08em] text-n-50">
              Where this chat runs
            </div>
            {AGENT_ORDER.map((agent) => {
              const capability = findAgent(control.agents, agent);
              if (!capability) return null;
              const selected = agent === control.agent;
              const drill = () => {
                pick(agent);
                setView(agent);
              };
              return (
                <div
                  key={agent}
                  role="button"
                  tabIndex={0}
                  onClick={drill}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      drill();
                    }
                  }}
                  className={cn(
                    ROW_CLASS,
                    "min-h-11 py-1.5 text-sm",
                    selected && "bg-accent/60",
                  )}
                >
                  <AgentRowLabel
                    agent={agent}
                    capability={capability}
                    onConnect={() => connect(agent)}
                  />
                  {selected && control.connected && settingsLabel && (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {settingsLabel}
                    </span>
                  )}
                  {selected && (
                    <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                  )}
                  <ChevronRight className="size-3.5 shrink-0 text-n-40" />
                </div>
              );
            })}
            <div className="px-4 pt-2 pb-1 text-xs leading-snug text-muted-foreground">
              Fixed once the chat starts.
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Step two: the agent's header (a back row while the agent can still change), then its settings. */
function AgentSettingsView({
  agent,
  control,
  tier,
  onTierChange,
  onBack,
  onConnect,
  onDone,
}: {
  agent: AgentId;
  control: HarnessControl;
  tier: AgentReasoningEffort;
  onTierChange?: (tier: AgentReasoningEffort) => void;
  onBack?: () => void;
  onConnect: () => void;
  onDone: () => void;
}) {
  const meta = AGENT_META[agent];
  const header = (
    <>
      {onBack && <ChevronLeft className="size-3.5 shrink-0 text-n-40" />}
      <meta.Logo className="size-4 shrink-0" />
      <div className="flex-1 truncate font-medium">{meta.label}</div>
    </>
  );
  // The pick lands on the control a render later; until then its settings are the old agent's.
  const landed = control.agent === agent;
  return (
    <>
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className={cn(ROW_CLASS, "min-h-9 py-1.5 text-sm")}
        >
          {header}
        </button>
      ) : (
        <div className="flex min-h-9 items-center gap-3 px-4 py-1.5 text-sm">
          {header}
          {control.locked && <Lock className="size-3 shrink-0 text-n-40" />}
        </div>
      )}
      {landed && !control.connected && meta.plan && (
        <div className="mx-1 mt-0.5 mb-1 flex items-center gap-2 rounded-lg bg-n-05 px-3 py-2 text-xs text-n-70">
          <span className="min-w-0 flex-1">Runs on your {meta.plan} plan.</span>
          <ConnectAgentButton onClick={onConnect} />
        </div>
      )}
      {landed && (
        <AgentSettingsPanel
          control={control}
          tier={tier}
          onTierChange={onTierChange}
          onDone={onDone}
        />
      )}
      {control.locked && (
        <div className="px-4 pt-2 pb-1 text-xs leading-snug text-muted-foreground">
          Fixed when this chat started. Start a new chat to use a different
          agent.
        </div>
      )}
    </>
  );
}
